import React from "react";
import { fmtDate } from "@/lib/format";

/**
 * On-screen invoice preview that MIRRORS the PDF layout produced by
 * `cgst_invoice_pdf.py`. Any changes to the PDF template should be
 * reflected here (and vice-versa) to keep customers seeing exactly
 * what will get printed / emailed.
 */
export default function InvoicePreview({ invoice, org, customer, einvoice, qrUrl }) {
  const inv = invoice || {};
  const isIntra = ((org?.state || "") === (customer?.state || "")) && !!(org?.state);
  const accent = org?.brand_color || "#EA580C";
  const rupees = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  const lines = inv.lines || [];
  const totalTaxable = lines.reduce((s, l) => s + Number(l.taxable_amount || 0), 0);
  const totalCGST = lines.reduce((s, l) => s + Number(l.cgst || 0), 0);
  const totalSGST = lines.reduce((s, l) => s + Number(l.sgst || 0), 0);
  const totalIGST = lines.reduce((s, l) => s + Number(l.igst || 0), 0);
  const totalDiscount = lines.reduce((s, l) => {
    const gross = Number(l.qty || 0) * Number(l.rate || 0);
    return s + gross * Number(l.discount_pct || 0) / 100;
  }, 0);
  const showDiscount = totalDiscount > 0.005;
  const grand = Number(inv.grand_total || 0);
  const tds = Number(inv.tds_amount || 0);
  const roundOff = Math.round(grand) - grand;
  const netGrand = Math.round((grand - tds + roundOff) * 100) / 100;

  const words = amountInWords(netGrand);

  const bank = org || {};
  const hasBank = bank.bank_name || bank.bank_account_number || bank.bank_ifsc;

  return (
    <div className="relative bg-white shadow-sm border rounded-sm p-6 md:p-8 max-w-[900px] mx-auto text-[13px] leading-relaxed" data-testid="invoice-preview" style={{ fontFamily: (org?.pdf_font === "Times-Roman") ? "'Times New Roman', serif" : (org?.pdf_font === "Courier") ? "'Courier New', monospace" : "system-ui, sans-serif" }}>
      {inv.status === "cancelled" && (
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center z-10 overflow-hidden" data-testid="invoice-cancelled-stamp">
          <div className="-rotate-[30deg] border-[6px] border-red-600/60 text-red-600/50 rounded-md px-8 py-3 text-center">
            <div className="text-6xl md:text-8xl font-black tracking-widest">CANCELLED</div>
            <div className="text-xs font-bold">{(inv.cancelled_at || "").slice(0, 10)} · {inv.cancel_reason}</div>
          </div>
        </div>
      )}
      {/* Header: Logo + Company */}
      <div className="flex items-start gap-4">
        {org?.logo_base64 ? (
          <img src={org.logo_base64} alt="logo" className="w-20 h-20 object-contain flex-shrink-0" />
        ) : (
          <div className="w-20 h-20 border-2 border-dashed border-muted-foreground/30 rounded-sm flex items-center justify-center text-[9px] text-muted-foreground text-center px-1 flex-shrink-0">Add logo in Settings</div>
        )}
        <div className="flex-1 min-w-0">
          <div className="font-bold text-lg">{org?.name || "Your Company"}</div>
          <div className="text-xs text-muted-foreground mt-0.5">{org?.address || ""}</div>
          <div className="text-xs text-muted-foreground">
            {org?.state ? `State: ${org.state}` : ""}
            {org?.pincode ? `  ·  PIN ${org.pincode}` : ""}
          </div>
          <div className="text-xs mt-1">
            <span className="text-muted-foreground">GSTIN:</span> <span className="font-mono font-semibold">{org?.gstin || "—"}</span>
            <span className="text-muted-foreground ml-3">PAN:</span> <span className="font-mono font-semibold">{org?.pan || "—"}</span>
          </div>
          {(org?.phone || org?.email) && (
            <div className="text-xs text-muted-foreground mt-0.5">
              {org?.phone || ""}{org?.phone && org?.email ? "  ·  " : ""}{org?.email || ""}
            </div>
          )}
        </div>
      </div>

      {/* TAX INVOICE title bar */}
      <div className="mt-4 rounded-sm text-center py-2 text-white font-bold tracking-widest" style={{ backgroundColor: accent }} data-testid="preview-title">
        TAX INVOICE
      </div>
      <div className="text-center text-[10px] uppercase tracking-widest text-muted-foreground mt-1">Original for Recipient · Issued under CGST Rule 46</div>

      {einvoice?.irn && (
        <div className="mt-2 border border-emerald-500 bg-emerald-50 rounded-sm px-3 py-1.5 text-[11px] text-emerald-800">
          <span className="font-bold">✓ IRN VERIFIED</span> · NIC IRP · Ack #{einvoice.ack_no || "—"} · {(einvoice.ack_date || "").slice(0, 16)}
        </div>
      )}

      {/* Meta + Parties */}
      <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-3">
        {/* Bill To */}
        <div className="border rounded-sm p-3">
          <div className="text-[9px] font-bold uppercase tracking-widest text-muted-foreground">Bill To</div>
          <div className="font-semibold mt-1">{customer?.display_name || customer?.name || "—"}</div>
          <div className="text-xs text-muted-foreground">
            {customer?.address_line1 || customer?.address || ""}
            {customer?.address_line2 ? <><br />{customer.address_line2}</> : null}
            {customer?.city ? <><br />{customer.city}{customer?.pincode ? ` - ${customer.pincode}` : ""}</> : null}
          </div>
          <div className="text-xs mt-1">State: <b>{customer?.state || "—"}</b>{customer?.state_code ? ` (${customer.state_code})` : ""}</div>
          <div className="text-xs">GSTIN: <span className="font-mono font-semibold">{customer?.gstin || "URD (Unregistered)"}</span></div>
          {customer?.pan && <div className="text-xs">PAN: <span className="font-mono font-semibold">{customer.pan}</span></div>}
        </div>
        {/* Ship To */}
        <div className="border rounded-sm p-3">
          <div className="text-[9px] font-bold uppercase tracking-widest text-muted-foreground">Ship To</div>
          {customer?.shipping_address && customer.shipping_address.trim() !== (customer?.address_line1 || customer?.address || "") ? (
            <>
              <div className="font-semibold mt-1">{customer?.display_name || customer?.name}</div>
              <div className="text-xs text-muted-foreground">{customer.shipping_address}</div>
              <div className="text-xs mt-1">State: <b>{customer?.shipping_state || customer?.state}</b></div>
            </>
          ) : (
            <div className="text-xs italic text-muted-foreground mt-1">Same as Bill To</div>
          )}
        </div>
        {/* Meta */}
        <div className="border rounded-sm p-3 text-xs">
          <div className="grid grid-cols-2 gap-y-1">
            <div className="text-muted-foreground">Invoice No.</div><div className="font-mono font-semibold">{inv.invoice_number}</div>
            <div className="text-muted-foreground">Date</div><div className="font-mono">{fmtDate(inv.date)}</div>
            {inv.due_date && (<><div className="text-muted-foreground">Due Date</div><div className="font-mono">{fmtDate(inv.due_date)}</div></>)}
            <div className="text-muted-foreground">Place of Supply</div><div className="font-mono">{inv.place_of_supply || "—"}</div>
            <div className="text-muted-foreground">Supply Type</div><div>{isIntra ? "Intra-state" : "Inter-state"}</div>
            <div className="text-muted-foreground">Reverse Charge</div><div className={inv.reverse_charge ? "font-bold text-orange-600" : ""}>{inv.reverse_charge ? "YES" : "No"}</div>
            {einvoice?.irn && (<><div className="text-muted-foreground">IRN</div><div className="font-mono truncate" title={einvoice.irn}>{(einvoice.irn || "").slice(0, 16)}…</div></>)}
            {einvoice?.ewb_no && (<><div className="text-muted-foreground">E-Way Bill</div><div className="font-mono">{einvoice.ewb_no}</div></>)}
          </div>
        </div>
      </div>

      {/* Line items */}
      <div className="mt-4 overflow-x-auto">
        <table className="w-full text-xs border-collapse">
          <thead>
            <tr className="text-white" style={{ backgroundColor: accent }}>
              <th className="p-2 text-center w-8">#</th>
              <th className="p-2 text-left">Description & HSN/SAC</th>
              <th className="p-2 text-right w-14">Qty</th>
              <th className="p-2 text-right w-20">Rate</th>
              {showDiscount && <th className="p-2 text-right w-14">Disc %</th>}
              <th className="p-2 text-right w-24">Taxable</th>
              {isIntra ? (
                <>
                  <th className="p-2 text-right w-20">CGST</th>
                  <th className="p-2 text-right w-20">SGST</th>
                </>
              ) : (
                <th className="p-2 text-right w-24">IGST</th>
              )}
              <th className="p-2 text-right w-24">Total</th>
            </tr>
          </thead>
          <tbody>
            {lines.map((l, i) => (
              <tr key={i} className={i % 2 ? "bg-gray-50" : "bg-white"}>
                <td className="p-2 text-center border">{i + 1}</td>
                <td className="p-2 border">
                  <div className="font-semibold">{l.description}</div>
                  {l.line_note && <div className="text-[11px] text-zinc-600 whitespace-pre-line">{l.line_note}</div>}
                  <div className="text-[10px] text-muted-foreground">HSN/SAC: <b>{l.hsn_code || "—"}</b>{l.is_service ? "" : ` · Unit: ${l.unit || "PCS"}`}</div>
                </td>
                <td className="p-2 text-right border tabular-nums">{l.is_service ? "—" : Number(l.qty).toFixed(2)}</td>
                <td className="p-2 text-right border tabular-nums">{rupees(l.rate)}</td>
                {showDiscount && <td className="p-2 text-right border tabular-nums">{Number(l.discount_pct) > 0.005 ? `${Number(l.discount_pct).toFixed(1)}%` : "—"}</td>}
                <td className="p-2 text-right border tabular-nums">{rupees(l.taxable_amount)}</td>
                {isIntra ? (
                  <>
                    <td className="p-2 text-right border tabular-nums">{rupees(l.cgst)}<div className="text-[9px] text-muted-foreground">({(Number(l.gst_rate || 0) / 2).toFixed(1)}%)</div></td>
                    <td className="p-2 text-right border tabular-nums">{rupees(l.sgst)}<div className="text-[9px] text-muted-foreground">({(Number(l.gst_rate || 0) / 2).toFixed(1)}%)</div></td>
                  </>
                ) : (
                  <td className="p-2 text-right border tabular-nums">{rupees(l.igst)}<div className="text-[9px] text-muted-foreground">({Number(l.gst_rate || 0).toFixed(1)}%)</div></td>
                )}
                <td className="p-2 text-right border tabular-nums font-semibold">{rupees(l.total)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Totals + amount in words + optional QR */}
      <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="border rounded-sm p-3">
          <div className="text-[9px] font-bold uppercase tracking-widest text-muted-foreground">Amount in Words</div>
          <div className="mt-1 font-semibold">{words}</div>
          {einvoice?.signed_qr_code && qrUrl && (
            <div className="mt-3 flex items-center gap-2">
              <img src={qrUrl} alt="Signed QR" className="w-24 h-24 border p-1 bg-white" data-testid="preview-qr" />
              <div className="text-[10px] text-muted-foreground">Signed QR by NIC IRP · scan to verify.</div>
            </div>
          )}
        </div>
        <div className="border rounded-sm">
          <table className="w-full text-sm">
            <tbody>
              {totalDiscount > 0.005 && (
                <tr><td className="p-2 text-muted-foreground">Total Discount</td><td className="p-2 text-right tabular-nums text-emerald-700">- {rupees(totalDiscount)}</td></tr>
              )}
              <tr><td className="p-2 text-muted-foreground">Subtotal (before tax)</td><td className="p-2 text-right tabular-nums">{rupees(totalTaxable)}</td></tr>
              {isIntra ? (
                <>
                  <tr><td className="p-2 text-muted-foreground">CGST Total</td><td className="p-2 text-right tabular-nums">{rupees(totalCGST)}</td></tr>
                  <tr><td className="p-2 text-muted-foreground">SGST Total</td><td className="p-2 text-right tabular-nums">{rupees(totalSGST)}</td></tr>
                </>
              ) : (
                <tr><td className="p-2 text-muted-foreground">IGST Total</td><td className="p-2 text-right tabular-nums">{rupees(totalIGST)}</td></tr>
              )}
              {tds > 0 && (
                <tr><td className="p-2 text-muted-foreground">Less: TDS u/s {inv.tds_section} @ {inv.tds_rate}%</td><td className="p-2 text-right tabular-nums">- {rupees(tds)}</td></tr>
              )}
              {Math.abs(roundOff) >= 0.01 && (
                <tr><td className="p-2 text-muted-foreground">Round Off</td><td className="p-2 text-right tabular-nums">{rupees(roundOff)}</td></tr>
              )}
              <tr className="bg-black text-white">
                <td className="p-3 font-bold uppercase tracking-widest">Grand Total (INR)</td>
                <td className="p-3 text-right tabular-nums font-bold text-base">{rupees(netGrand)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* Bank Details + Signatory */}
      <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="border rounded-sm p-3">
          <div className="text-[9px] font-bold uppercase tracking-widest text-muted-foreground">Bank Details for Payment</div>
          {hasBank ? (
            <div className="mt-1 text-xs space-y-0.5">
              {bank.bank_name && <div className="font-semibold">{bank.bank_name}</div>}
              {bank.bank_account_name && <div>A/c Name: {bank.bank_account_name}</div>}
              {bank.bank_account_number && <div>A/c No: <span className="font-mono font-semibold">{bank.bank_account_number}</span></div>}
              {bank.bank_ifsc && <div>IFSC: <span className="font-mono font-semibold">{bank.bank_ifsc}</span></div>}
              {bank.bank_branch && <div>Branch: {bank.bank_branch}</div>}
              {bank.bank_upi && <div>UPI: <span className="font-mono font-semibold">{bank.bank_upi}</span></div>}
            </div>
          ) : (
            <div className="mt-2 text-[11px] italic text-orange-600">
              Not configured. Add in Settings → Branding &amp; Templates → Bank Details.
            </div>
          )}
        </div>
        <div className="border rounded-sm p-3 text-right">
          <div className="text-xs text-muted-foreground">For <b>{org?.name || ""}</b></div>
          <div className="h-16" />
          <div className="border-t inline-block pt-1 min-w-[180px]">
            <div className="text-sm font-semibold">{org?.authorised_signatory_name || "Authorised Signatory"}</div>
            {org?.authorised_signatory_designation && <div className="text-[10px] text-muted-foreground">{org.authorised_signatory_designation}</div>}
            <div className="text-[10px] text-muted-foreground">Authorised Signatory</div>
          </div>
        </div>
      </div>

      {/* Terms + Declaration */}
      <div className="mt-4 border-t pt-3">
        <div className="text-[9px] font-bold uppercase tracking-widest text-muted-foreground">Terms &amp; Conditions</div>
        <div className="text-[11px] text-muted-foreground mt-1">{inv.terms || "Payment due within 30 days. Interest @18% p.a. on overdue amounts."}</div>
        <div className="text-[9px] font-bold uppercase tracking-widest text-muted-foreground mt-3">Declaration</div>
        <div className="text-[11px] text-muted-foreground mt-1">We declare that this invoice shows the actual price of the goods/services described and that all particulars are true and correct.{org?.invoice_footer_note ? ` ${org.invoice_footer_note}` : ""}</div>
      </div>

      <div className="mt-4 text-center text-[9px] text-muted-foreground">This is a computer-generated tax invoice.</div>
    </div>
  );
}

// Indian numbering (crore / lakh / thousand) — mirrors PDF _amount_in_words.
function amountInWords(amount) {
  const _one = (n) => {
    const w = ["", "One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight", "Nine",
               "Ten", "Eleven", "Twelve", "Thirteen", "Fourteen", "Fifteen", "Sixteen",
               "Seventeen", "Eighteen", "Nineteen"];
    const t = ["", "", "Twenty", "Thirty", "Forty", "Fifty", "Sixty", "Seventy", "Eighty", "Ninety"];
    n = parseInt(n, 10);
    if (n < 20) return w[n];
    if (n < 100) return t[Math.floor(n / 10)] + (n % 10 ? " " + w[n % 10] : "");
    return w[Math.floor(n / 100)] + " Hundred" + (n % 100 ? " and " + _one(n % 100) : "");
  };
  let n = Math.round(amount);
  if (n === 0) return "Zero Rupees Only";
  const parts = [];
  const cr = Math.floor(n / 10000000); if (cr) { parts.push(_one(cr) + " Crore"); n %= 10000000; }
  const lk = Math.floor(n / 100000); if (lk) { parts.push(_one(lk) + " Lakh"); n %= 100000; }
  const th = Math.floor(n / 1000); if (th) { parts.push(_one(th) + " Thousand"); n %= 1000; }
  if (n) parts.push(_one(n));
  let words = parts.join(" ").trim();
  const paise = Math.round((amount - Math.floor(amount)) * 100);
  words += " Rupees";
  if (paise) words += " and " + _one(paise) + " Paise";
  return words + " Only";
}
